import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Inbox, AlertTriangle, Eye } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const FILTERS = [
  { value: "all", label: "All submissions", params: {} },
  { value: "consultation", label: "Charity consultations", params: { kind: "consultation" } },
  { value: "fundraiser", label: "Fundraiser interest", params: { kind: "fundraiser" } },
  { value: "contact", label: "General enquiries & complaints", params: { kind: "contact" } },
  { value: "complaints", label: "Complaints only", params: { complaints_only: true } },
];

const KEY_LABELS = {
  organisation_name: "Charity / organisation",
  contact_name: "Contact name",
  full_name: "Full name",
  role: "Role",
  email: "Email",
  telephone: "Telephone",
  charity_number: "Charity number",
  location: "Operating location / area",
  support_type: "Support required",
  start_date: "Anticipated start date",
  organisation: "Organisation",
  enquiry_type: "Enquiry type",
  area: "General area available",
  driver: "Driver",
  experience: "Relevant experience",
  availability: "Availability",
  dbs_confirm: "DBS check consent",
  message: "Message",
};

function typeLabel(s) {
  if (s.kind === "consultation") return "Charity consultation";
  if (s.kind === "fundraiser") return "Fundraiser interest";
  if (s.is_complaint) return "Complaint";
  return "General enquiry";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs ${
        ok ? "bg-brand-green/10 text-brand-greenDeep" : "bg-cream text-slate-muted"
      }`}
    >
      {label}: {ok ? "Sent" : "Not sent"}
    </span>
  );
}

function humanValue(key, value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

export default function SubmissionsPanel() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (value) => {
    setLoading(true);
    try {
      const params = FILTERS.find((f) => f.value === value)?.params || {};
      const { data } = await api.get("/admin/submissions", { params });
      setItems(data.submissions || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filter);
  }, [filter, load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const { data } = await api.get(`/admin/submissions/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load the submission.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <section className="mt-16" data-testid="submissions-panel">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-medium tracking-tight text-navy sm:text-3xl">
            Submissions
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-body">
            Every consultation request, fundraiser-interest form, general enquiry and complaint
            submitted through the website. Newest first.
          </p>
        </div>
        <div>
          <label htmlFor="submissions-filter" className="mb-1.5 block text-xs font-medium text-slate-muted">
            Filter by type
          </label>
          <select
            id="submissions-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-11 w-full min-w-[16rem] rounded-none border border-line bg-white px-3 text-sm text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
            data-testid="submissions-filter"
          >
            {FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 border border-line bg-white">
        {loading ? (
          <div className="flex items-center gap-3 p-8 text-sm text-slate-muted" data-testid="submissions-loading">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-brand-green" />
            Loading submissions…
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-12 text-center" data-testid="submissions-empty">
            <Inbox className="h-8 w-8 text-slate-muted" />
            <p className="text-sm text-slate-muted">No submissions to show for this filter yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-line" data-testid="submissions-list">
            {items.map((s) => (
              <li
                key={s.id}
                className={`p-5 sm:px-6 ${s.is_complaint ? "border-l-2 border-l-red-500 bg-red-50/40" : ""}`}
                data-testid={`submission-row-${s.id}`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold ${
                          s.is_complaint ? "bg-red-600 text-white" : "bg-navy text-white"
                        }`}
                        data-testid={`submission-type-${s.id}`}
                      >
                        {s.is_complaint && <AlertTriangle className="h-3 w-3" />}
                        {typeLabel(s)}
                      </span>
                      <span className="text-xs text-slate-muted" data-testid={`submission-date-${s.id}`}>
                        {formatDate(s.created_at)}
                      </span>
                    </div>
                    <p className="mt-2 truncate font-medium text-navy">
                      {s.name}
                      {s.organisation ? <span className="text-slate-muted"> · {s.organisation}</span> : null}
                    </p>
                    <p className="mt-0.5 truncate text-sm text-slate-body">
                      {s.email || "—"}
                      {s.telephone ? ` · ${s.telephone}` : ""}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <StatusPill ok={s.email_sent} label="Notification" />
                      <StatusPill ok={s.ack_sent} label="Acknowledgement" />
                    </div>
                  </div>
                  <button
                    onClick={() => openDetail(s.id)}
                    className="inline-flex shrink-0 items-center gap-2 border border-line px-4 py-2 text-sm font-medium text-navy transition-colors hover:bg-navy hover:text-white"
                    data-testid={`submission-view-${s.id}`}
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-none sm:max-w-2xl" data-testid="submission-detail">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">
              {detail && detail.kind ? typeLabel(detail) : "Submission"}
            </DialogTitle>
            <DialogDescription>
              {detail?.created_at ? `Submitted ${formatDate(detail.created_at)}` : "Full submitted information"}
            </DialogDescription>
          </DialogHeader>

          {detailLoading || !detail?.data ? (
            <div className="flex items-center gap-3 py-8 text-sm text-slate-muted">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-brand-green" />
              Loading…
            </div>
          ) : (
            <div>
              <div className="mb-4 flex flex-wrap gap-2">
                <StatusPill ok={detail.email_sent} label="Staff notification" />
                <StatusPill ok={detail.ack_sent} label="Acknowledgement" />
              </div>
              <dl className="divide-y divide-line border border-line">
                {Object.entries(detail.data).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-1 gap-1 p-3 sm:grid-cols-3 sm:gap-4">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-muted">
                      {KEY_LABELS[k] || k.replace(/_/g, " ")}
                    </dt>
                    <dd className="whitespace-pre-wrap break-words text-sm text-navy sm:col-span-2">
                      {humanValue(k, v)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
